import { ReadonlyVec3 } from 'gl-matrix';
import { THRESHOLD_SEGMENTATION_PRESETS } from '~/components/toolbar/OperationsToolbar/bodyContour/thresholdSegmentationPresets';
import { PointMembership } from '~/domain/geometry/contourStackMembership';
import { Image3D } from '~/domain/patient/image3d';
import { Grid } from '~/engine/data/common/Grid';
import {
    bodyContourTextureFromMask,
    extractBodyContourMask,
} from '~/engine/util/algorithms/sdf/BodyContourExtraction';
import { getSigmaForStrength } from '~/engine/util/algorithms/sdf/GaussianSmoothing';
import { SDF_ISO_THRESHOLD } from '~/engine/util/algorithms/sdf/sdfConstants';

// The Body tool's own preset: one definition of "the body" across the app.
const BODY = THRESHOLD_SEGMENTATION_PRESETS.body;

// Inside where the field reaches the iso-value; outside the field's grid is outside the body.
function sdfMembership(
    slices: readonly Uint8Array[],
    grid: Pick<Grid, 'toVoxelSpace' | 'sizeX' | 'sizeY' | 'sizeZ'>
): PointMembership {
    const { sizeX, sizeY, sizeZ } = grid;
    return (pointMm: ReadonlyVec3) => {
        const voxel = grid.toVoxelSpace(pointMm);
        const x = Math.round(voxel[0]);
        const y = Math.round(voxel[1]);
        const z = Math.round(voxel[2]);
        if (x < 0 || y < 0 || z < 0 || x >= sizeX || y >= sizeY || z >= sizeZ) {
            return false;
        }
        return slices[z][y * sizeX + x] >= SDF_ISO_THRESHOLD;
    };
}

// The scan's body as the Body tool segments it: couch and loose objects dropped, internal air filled.
export async function generateBodyMembership(
    image: Image3D
): Promise<PointMembership | undefined> {
    if (!image.isHuQuantified) {
        return undefined;
    }
    const mask = await extractBodyContourMask({
        imageGrid: image.grid,
        floatPixelData: image.floatPixelData,
        thresholdHu: BODY.threshold.defaultHu,
        fillCavities2d: BODY.fillHoles2d.defaultEnabled,
    });
    if (mask.isEmpty) {
        return undefined;
    }
    const texture = await bodyContourTextureFromMask(image.grid, mask, {
        smoothingSigma: BODY.smoothing.defaultEnabled
            ? getSigmaForStrength(BODY.smoothing.defaultStrength)
            : undefined,
        minPartAreaMm2: BODY.minPartSize.defaultEnabled
            ? BODY.minPartSize.defaultAreaMm2
            : undefined,
        keepLargest2dCount: BODY.keepLargest2d.defaultEnabled
            ? BODY.keepLargest2d.defaultCount
            : undefined,
        keepLargest3dCount: BODY.keepLargest3d.defaultEnabled
            ? BODY.keepLargest3d.defaultCount
            : undefined,
        finalFillHoles2d: BODY.finalFillHoles2d.defaultEnabled || undefined,
    });
    if (!texture) {
        return undefined;
    }
    try {
        if (texture.isEmpty) {
            return undefined;
        }
        // Copied out before the texture's GPU storage is released.
        const slices = texture.signedDistanceField.pixels.map((slice) =>
            slice.slice()
        );
        return sdfMembership(slices, texture.textureGrid);
    } finally {
        texture.delete();
    }
}
